// T-REX field: the Velaris-style WebGL background in brand colours. Simplex
// noise, four-colour blend, vignette glow and film grain in one shader.
// Ported verbatim from the hand-built articles.html design as an ES module.
// mountField(canvas, "hero" | "banner" | "cover" | {options}) -> { destroy() }
const BRAND = { bg: "#15102A", colors: ["#4C3778", "#5B4590", "#2A1C4A", "#15102A"] };

const PRESETS = {
  hero: { ...BRAND, speed: 1.1, grain: 0.3, still: false, scale: 0.5 },
  banner: { ...BRAND, speed: 0.9, grain: 0.3, still: false, scale: 0.5 },
  cover: { ...BRAND, speed: 1, grain: 0.3, still: true, scale: 0.75 },
};

const VS = `attribute vec2 position;varying vec2 vUv;void main(){vUv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;

const FS = `precision highp float;varying vec2 vUv;
uniform vec2 u_resolution;uniform float u_time;uniform float u_grain;uniform vec3 u_colors[4];uniform vec3 u_bg;
vec3 permute(vec3 x){return mod(((x*34.0)+1.0)*x,289.0);}
float snoise(vec2 v){
  const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
  vec4 x12=x0.xyxy+C.xxzz;x12.xy-=i1;i=mod(i,289.0);
  vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
  vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);m=m*m;m=m*m;
  vec3 x=2.0*fract(p*C.www)-1.0;vec3 h=abs(x)-0.5;vec3 ox=floor(x+0.5);vec3 a0=x-ox;
  m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
  vec3 g;g.x=a0.x*x0.x+h.x*x0.y;g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.0*dot(m,g);}
void main(){
  vec2 uv=vUv;float ratio=u_resolution.x/u_resolution.y;vec2 p=uv-0.5;p.x*=ratio;
  float t=u_time*0.1;
  float n1=snoise(p*0.32+vec2(t*0.2,-t*0.3));
  float n2=snoise(p*0.55+vec2(-t*0.15,t*0.25)+n1*0.25);
  float n3=snoise(p*0.75+vec2(t*0.1,-t*0.2)+n2*0.2);
  vec3 col=u_bg;
  float dist=length(p)*1.5;
  float vignette=1.0-smoothstep(1.0,2.2,dist);
  col=mix(col,u_colors[0],smoothstep(-0.2,0.5,n1)*0.85);
  col=mix(col,u_colors[1],smoothstep(-0.1,0.6,n2)*0.7);
  col=mix(col,u_colors[2],smoothstep(-0.3,0.4,n3)*0.6);
  col=mix(col,u_colors[3],smoothstep(0.0,0.7,n1*n2)*0.5);
  float glow=(smoothstep(0.9,0.0,length(p-vec2(-0.9,0.25)))+smoothstep(0.9,0.0,length(p-vec2(0.8,-0.2)))+smoothstep(0.7,0.0,length(p-vec2(0.1,0.35))))*0.18;
  col+=u_colors[1]*glow;
  col=mix(col*0.55,col,vignette);
  float grain=fract(sin(dot(uv,vec2(12.9898,78.233)))*43758.5453+u_time);
  col+=(grain-0.5)*u_grain*0.1;
  gl_FragColor=vec4(col,1.0);}`;

const hex = (h) => {
  const s = h.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16) / 255);
};

export function mountField(cv, opt) {
  const o = { ...PRESETS.hero, ...(typeof opt === "string" ? PRESETS[opt] : opt || {}) };
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const gl = cv.getContext("webgl", { antialias: false, depth: false, stencil: false, alpha: false, powerPreference: "low-power" });
  if (!gl) {
    cv.style.background = "radial-gradient(ellipse 70% 70% at 0% 0%,rgba(76,55,120,.3),transparent 62%)," + o.bg;
    return { destroy() {} };
  }
  const sh = (ty, src) => {
    const s = gl.createShader(ty);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return s;
  };
  const pr = gl.createProgram();
  gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS));
  gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(pr);
  gl.useProgram(pr);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(pr, "position");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const U = (n) => gl.getUniformLocation(pr, n);
  gl.uniform1f(U("u_grain"), o.grain);
  gl.uniform3f(U("u_bg"), ...hex(o.bg));
  gl.uniform3fv(U("u_colors"), new Float32Array(o.colors.slice(0, 4).flatMap(hex)));
  const uR = U("u_resolution"), uT = U("u_time");
  const scale = Math.min(window.devicePixelRatio || 1, 2) * o.scale;
  const host = cv.parentElement || cv;
  let visible = true, raf = 0;
  const t0 = performance.now() - (o.seed != null ? o.seed * 1000 : Math.random() * 600000);

  function draw(now) {
    const w = Math.max(1, Math.round(cv.clientWidth * scale));
    const h = Math.max(1, Math.round(cv.clientHeight * scale));
    if (cv.width !== w || cv.height !== h) {
      cv.width = w; cv.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(uR, w, h);
    }
    gl.uniform1f(uT, (now - t0) * 0.001 * o.speed);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  const still = o.still || reduce || o.speed === 0;
  function frame(now) {
    draw(now);
    raf = visible && !still && !document.hidden ? requestAnimationFrame(frame) : 0;
  }
  const start = () => { if (!raf) raf = requestAnimationFrame(frame); };
  const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) start(); });
  io.observe(host);
  const onVis = () => { if (!document.hidden) start(); };
  document.addEventListener("visibilitychange", onVis);
  const onResize = () => { if (still) draw(performance.now()); };
  window.addEventListener("resize", onResize, { passive: true });
  if (still) draw(performance.now()); else start();

  return {
    destroy() {
      cancelAnimationFrame(raf); raf = 0;
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("resize", onResize);
    },
  };
}
